import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FormulariosHeader from "../components/FormulariosHeader";
import {
  cancelTreasuryFormAssignment,
  createTreasuryFormAssignments,
  getTreasuryFormById,
  listTreasuryFormAssignments,
  listTreasuryFormEmployeeOptions,
} from "../api/treasuryFormsApi";
import {
  formatDate,
  formatDateTime,
  getSubmissionStatusLabel,
} from "../utils/treasuryFormsFormatters";

const TreasurySubmissionDetailModal = lazy(() =>
  import("../components/TreasurySubmissionDetailModal")
);

const ASSIGNMENTS_PAGE_SIZE = 25;

function AssignmentStatusChip({ status }) {
  let bgcolor = "rgba(20, 32, 82, 0.08)";
  let color = "neutral.700";

  if (status === "IN_REVIEW") {
    bgcolor = "rgba(255, 179, 0, 0.18)";
    color = "warning.dark";
  } else if (status === "APPROVED") {
    bgcolor = "rgba(185, 246, 202, 0.72)";
    color = "primary.900";
  } else if (status === "CANCELLED") {
    bgcolor = "rgba(239, 83, 80, 0.18)";
    color = "error.main";
  }

  return (
    <Chip
      label={getSubmissionStatusLabel(status)}
      size="small"
      sx={{
        borderRadius: "10px",
        fontWeight: 800,
        bgcolor,
        color,
      }}
    />
  );
}

function getTodayString() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function TesoreriaFormulariosGestionarPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { formId } = useParams();
  const listState = location.state?.listState;
  const returnTo = location.state?.returnTo || "/tesoreria/formularios";

  const isValidId = Boolean(formId && /^\d+$/.test(String(formId)) && Number(formId) > 0);

  // Form details state
  const [formDetail, setFormDetail] = useState(null);
  const [formLoading, setFormLoading] = useState(true);
  const [formError, setFormError] = useState("");

  // Assignment creation panel state
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeError, setEmployeeError] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [sectorInput, setSectorInput] = useState("");
  const [operationalDateInput, setOperationalDateInput] = useState(getTodayString());
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignResult, setAssignResult] = useState(null);
  const [assignError, setAssignError] = useState("");

  // Assignments table state
  const [assignments, setAssignments] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState("");
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  // Cancel assignment dialog state
  const [cancelAssignmentTarget, setCancelAssignmentTarget] = useState(null);
  const [cancelAssignmentLoading, setCancelAssignmentLoading] = useState(false);
  const [cancelAssignmentError, setCancelAssignmentError] = useState("");

  // Detail / Response modal state
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);

  useEffect(() => {
    if (!isValidId) {
      navigate(returnTo, { replace: true, state: { listState } });
    }
  }, [isValidId, listState, navigate, returnTo]);

  // Load form details
  const fetchForm = useCallback(async (signal) => {
    if (!isValidId) return;
    setFormLoading(true);
    setFormError("");
    try {
      const res = await getTreasuryFormById(formId, { signal });
      const payload = res?.data && !("id" in res) ? res.data : res;
      setFormDetail(payload);
    } catch (err) {
      if (signal?.aborted) return;
      setFormError(
        err?.response?.data?.message ||
          err?.data?.message ||
          "No se pudo cargar la información del formulario."
      );
    } finally {
      if (!signal?.aborted) {
        setFormLoading(false);
      }
    }
  }, [formId, isValidId]);

  // Search employee options
  const fetchEmployees = useCallback(async (search = "", signal) => {
    if (!isValidId) return;
    setEmployeeLoading(true);
    setEmployeeError("");
    try {
      const res = await listTreasuryFormEmployeeOptions(formId, {
        search,
        page: 1,
        limit: 50,
        signal,
      });
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setEmployeeOptions(list);
    } catch (err) {
      if (signal?.aborted) return;
      setEmployeeOptions([]);
      setEmployeeError(
        err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          "No se pudieron cargar los empleados disponibles."
      );
    } finally {
      if (!signal?.aborted) {
        setEmployeeLoading(false);
      }
    }
  }, [formId, isValidId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearch.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [employeeSearch]);

  // Load assignments table
  const fetchAssignments = useCallback(async (signal) => {
    if (!isValidId) return;
    setTableLoading(true);
    setTableError("");
    try {
      const res = await listTreasuryFormAssignments(formId, {
        page: page + 1,
        limit: ASSIGNMENTS_PAGE_SIZE,
        status: statusFilter || undefined,
        signal,
      });
      setAssignments(res?.data || []);
      setTotalItems(res?.meta?.totalItems || 0);
    } catch (err) {
      if (signal?.aborted) return;
      setTableError(
        err?.response?.data?.message ||
          err?.data?.message ||
          "No se pudieron cargar las asignaciones del formulario."
      );
    } finally {
      if (!signal?.aborted) {
        setTableLoading(false);
      }
    }
  }, [formId, isValidId, page, statusFilter]);

  useEffect(() => {
    const controller = new AbortController();
    fetchForm(controller.signal);
    return () => controller.abort();
  }, [fetchForm]);

  useEffect(() => {
    const controller = new AbortController();
    fetchEmployees(debouncedEmployeeSearch, controller.signal);
    return () => controller.abort();
  }, [fetchEmployees, debouncedEmployeeSearch]);

  useEffect(() => {
    const controller = new AbortController();
    fetchAssignments(controller.signal);
    return () => controller.abort();
  }, [fetchAssignments]);

  const handleBack = () => {
    navigate(returnTo, { state: { listState } });
  };

  const isFormArchived = formDetail?.status === "ARCHIVED";
  const isSectorTooLong = sectorInput.length > 100;
  const isAssignDisabled =
    assignSubmitting ||
    selectedEmployees.length === 0 ||
    !operationalDateInput ||
    isSectorTooLong ||
    isFormArchived;

  // Submit batch assignments
  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (selectedEmployees.length === 0) {
      setAssignError("Debes seleccionar al menos un empleado.");
      return;
    }
    if (!operationalDateInput) {
      setAssignError("La fecha operativa es obligatoria.");
      return;
    }
    if (isSectorTooLong) {
      setAssignError("El sector no puede superar los 100 caracteres.");
      return;
    }

    setAssignSubmitting(true);
    setAssignError("");
    setAssignResult(null);

    try {
      const currentSelected = [...selectedEmployees];
      const payload = {
        employeeIds: currentSelected.map((emp) => emp.id),
        sector: sectorInput.trim() || undefined,
        operationalDate: operationalDateInput,
      };
      const res = await createTreasuryFormAssignments(formId, payload);
      setAssignResult(res);

      if (res?.created?.length > 0) {
        fetchAssignments();
        if (!res?.rejected || res.rejected.length === 0) {
          setSelectedEmployees([]);
        } else {
          // Keep only rejected employees in the selection
          const rejectedIds = new Set(res.rejected.map((r) => r.employeeId));
          setSelectedEmployees((prev) => prev.filter((emp) => rejectedIds.has(emp.id)));
        }
      }
    } catch (err) {
      setAssignError(
        err?.response?.data?.message ||
          err?.data?.message ||
          "No se pudieron crear las asignaciones."
      );
    } finally {
      setAssignSubmitting(false);
    }
  };

  // Cancel assignment action
  const handleConfirmCancelAssignment = async () => {
    if (!cancelAssignmentTarget?.id) return;
    setCancelAssignmentLoading(true);
    setCancelAssignmentError("");
    try {
      await cancelTreasuryFormAssignment(cancelAssignmentTarget.id);
      setCancelAssignmentTarget(null);
      fetchAssignments();
    } catch (err) {
      setCancelAssignmentError(
        err?.response?.data?.message ||
          err?.data?.message ||
          "No se pudo cancelar la asignación."
      );
    } finally {
      setCancelAssignmentLoading(false);
    }
  };

  if (!isValidId) return null;

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Stack spacing={2.5}>
        <FormulariosHeader
          title={formDetail ? formDetail.name : `Gestionar Formulario #${formId}`}
          onBack={handleBack}
        />

        {formLoading ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: "16px" }}>
            <CircularProgress size={30} />
            <Typography sx={{ mt: 1.5, fontSize: 13, color: "neutral.700" }}>
              Cargando información del formulario...
            </Typography>
          </Paper>
        ) : formError ? (
          <Alert severity="error" sx={{ borderRadius: "14px" }}>
            {formError}
          </Alert>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "420px 1fr" },
              gap: 2.5,
              alignItems: "start",
            }}
          >
            {/* Left Column: Assignment Creation Panel */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                bgcolor: "rgba(255, 255, 255, 0.95)",
                border: "1px solid rgba(20, 32, 82, 0.08)",
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 900, color: "primary.900", mb: 2 }}>
                Asignar Formulario a Empleados
              </Typography>

              {isFormArchived ? (
                <Alert severity="warning" sx={{ borderRadius: "12px" }}>
                  Este formulario se encuentra archivado y no puede recibir nuevas asignaciones.
                </Alert>
              ) : (
                <Box component="form" onSubmit={handleAssignSubmit}>
                  <Stack spacing={2}>
                    {/* Autocomplete Employees */}
                    <Autocomplete
                      multiple
                      options={employeeOptions}
                      getOptionLabel={(option) =>
                        `${option.fullName}${option.dni ? ` (DNI ${option.dni})` : ""}`
                      }
                      isOptionEqualToValue={(option, val) => option.id === val.id}
                      value={selectedEmployees}
                      onChange={(_, newValue) => setSelectedEmployees(newValue)}
                      loading={employeeLoading}
                      onInputChange={(_, newInputValue) => setEmployeeSearch(newInputValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seleccionar empleados *"
                          placeholder="Buscar por nombre o DNI..."
                          size="small"
                        />
                      )}
                      renderTags={() => null} // Render explicit list below
                    />

                    {employeeError ? (
                      <Alert
                        severity="error"
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            onClick={() => fetchEmployees(debouncedEmployeeSearch)}
                          >
                            Reintentar
                          </Button>
                        }
                        sx={{ borderRadius: "10px" }}
                      >
                        {employeeError}
                      </Alert>
                    ) : null}

                    {/* Explicit list of selected employees with removal */}
                    {selectedEmployees.length > 0 ? (
                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: "10px",
                          bgcolor: "neutral.50",
                          border: "1px solid rgba(20, 32, 82, 0.08)",
                          maxHeight: 140,
                          overflowY: "auto",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "neutral.600", mb: 0.8 }}>
                          Empleados seleccionados ({selectedEmployees.length}):
                        </Typography>
                        <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                          {selectedEmployees.map((emp) => (
                            <Chip
                              key={emp.id}
                              label={emp.fullName}
                              size="small"
                              onDelete={() =>
                                setSelectedEmployees((prev) => prev.filter((e) => e.id !== emp.id))
                              }
                              sx={{ fontWeight: 600, fontSize: 11.5 }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    ) : null}

                    {/* Sector Input */}
                    <TextField
                      size="small"
                      label="Sector (opcional)"
                      placeholder="Ej: Turno Tarde"
                      value={sectorInput}
                      onChange={(e) => setSectorInput(e.target.value)}
                      error={isSectorTooLong}
                      helperText={
                        isSectorTooLong
                          ? "El sector no puede superar los 100 caracteres"
                          : `${sectorInput.length}/100`
                      }
                      inputProps={{ maxLength: 100 }}
                    />

                    {/* Operational Date */}
                    <TextField
                      size="small"
                      type="date"
                      label="Fecha Operativa *"
                      InputLabelProps={{ shrink: true }}
                      value={operationalDateInput}
                      onChange={(e) => setOperationalDateInput(e.target.value)}
                      required
                    />

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isAssignDisabled}
                      startIcon={<PersonAddAlt1Icon />}
                      sx={{
                        height: 40,
                        borderRadius: "20px",
                        textTransform: "none",
                        fontWeight: 800,
                        px: 3,
                        bgcolor: "primary.500",
                        color: "#fff",
                        "&:hover": { bgcolor: "primary.600" },
                      }}
                    >
                      {assignSubmitting ? (
                        <CircularProgress size={20} />
                      ) : selectedEmployees.length === 1 ? (
                        "Asignar a 1 empleado"
                      ) : (
                        `Asignar a ${selectedEmployees.length} empleados`
                      )}
                    </Button>

                    {/* Result Alerts */}
                    {assignError ? (
                      <Alert severity="error" sx={{ borderRadius: "10px" }}>
                        {assignError}
                      </Alert>
                    ) : null}

                    {assignResult ? (
                      <Stack spacing={1}>
                        {assignResult.created?.length > 0 ? (
                          <Alert severity="success" sx={{ borderRadius: "10px" }}>
                            Se crearon exitosamente {assignResult.created.length} asignación(es).
                          </Alert>
                        ) : null}

                        {assignResult.rejected?.length > 0 ? (
                          <Alert severity="warning" sx={{ borderRadius: "10px" }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                              {assignResult.rejected.length} asignación(es) no pudieron crearse:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0, fontSize: 12 }}>
                              {assignResult.rejected.map((rej, idx) => {
                                const resolvedName =
                                  selectedEmployees.find((e) => e.id === rej.employeeId)?.fullName ||
                                  employeeOptions.find((e) => e.id === rej.employeeId)?.fullName ||
                                  rej.employeeName ||
                                  `Empleado #${rej.employeeId}`;
                                return (
                                  <li key={idx}>
                                    <strong>{resolvedName}:</strong> {rej.message}
                                  </li>
                                );
                              })}
                            </Box>
                          </Alert>
                        ) : null}
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>
              )}
            </Paper>

            {/* Right Column: Assignments List Table */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "16px",
                bgcolor: "rgba(255, 255, 255, 0.95)",
                border: "1px solid rgba(20, 32, 82, 0.08)",
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Typography sx={{ fontSize: 16, fontWeight: 900, color: "primary.900" }}>
                    Historial de Asignaciones ({totalItems})
                  </Typography>

                  <Box sx={{ width: 180 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="status-filter-label">Estado</InputLabel>
                      <Select
                        labelId="status-filter-label"
                        label="Estado"
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="PENDING">Sin respuesta</MenuItem>
                        <MenuItem value="IN_REVIEW">En revisión</MenuItem>
                        <MenuItem value="APPROVED">Aprobado</MenuItem>
                        <MenuItem value="CANCELLED">Cancelado</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Stack>

                {tableError ? (
                  <Alert severity="error" sx={{ borderRadius: "10px" }}>
                    {tableError}
                  </Alert>
                ) : null}

                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    border: "1px solid rgba(20, 32, 82, 0.10)",
                    borderRadius: "12px",
                  }}
                >
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "neutral.100" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                          Empleado
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                          Sector
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                          Fecha Asignada
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                          Fecha Operativa
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                          Estado
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: 12 }}>
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <CircularProgress size={24} />
                            <Typography sx={{ fontSize: 12, color: "neutral.600", mt: 1 }}>
                              Cargando asignaciones...
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : assignments.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={{ py: 4, color: "neutral.600", fontSize: 13 }}
                          >
                            No se encontraron asignaciones para este formulario.
                          </TableCell>
                        </TableRow>
                      ) : (
                        assignments.map((asg) => {
                          const hasSubmission = asg.hasSubmission === true;
                          const canCancelAssignment = asg.status === "PENDING" && !hasSubmission;

                          return (
                            <TableRow key={asg.id} hover>
                              <TableCell sx={{ fontSize: 12.5, fontWeight: 600 }}>
                                {asg.employee?.fullName || `Empleado #${asg.employeeId}`}
                                {asg.employee?.dni ? (
                                  <Typography sx={{ fontSize: 11, color: "neutral.600" }}>
                                    DNI {asg.employee.dni}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell sx={{ fontSize: 12.5 }}>
                                {asg.sector || "—"}
                              </TableCell>
                              <TableCell sx={{ fontSize: 11.5, color: "neutral.700" }}>
                                {formatDateTime(asg.assignedAt || asg.createdAt)}
                                {asg.assignedByUser?.displayName ? (
                                  <Typography sx={{ fontSize: 10.5, color: "neutral.500" }}>
                                    por {asg.assignedByUser.displayName}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell sx={{ fontSize: 12.5 }}>
                                {formatDate(asg.operationalDate)}
                              </TableCell>
                              <TableCell>
                                <AssignmentStatusChip status={asg.status} />
                              </TableCell>
                              <TableCell align="right">
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  justifyContent="flex-end"
                                >
                                  {/* Always render Ver action, disabled if hasSubmission is false */}
                                  <Tooltip
                                    title={
                                      hasSubmission
                                        ? "Ver respuesta"
                                        : "Sin respuesta enviada"
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        disabled={!hasSubmission}
                                        onClick={() => setSelectedAssignmentId(asg.id)}
                                        aria-label="Ver respuesta"
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  {/* Cancel action only for PENDING && !hasSubmission */}
                                  {canCancelAssignment ? (
                                    <Tooltip title="Cancelar asignación">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          setCancelAssignmentError("");
                                          setCancelAssignmentTarget(asg);
                                        }}
                                        aria-label="Cancelar asignación"
                                      >
                                        <CancelOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalItems}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={ASSIGNMENTS_PAGE_SIZE}
                  rowsPerPageOptions={[]}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                />
              </Stack>
            </Paper>
          </Box>
        )}
      </Stack>

      {/* Shared Response / Submission Modal */}
      {selectedAssignmentId ? (
        <Suspense fallback={null}>
          <TreasurySubmissionDetailModal
            open
            assignmentId={selectedAssignmentId}
            onClose={() => setSelectedAssignmentId(null)}
            onSuccess={() => {
              fetchAssignments();
            }}
          />
        </Suspense>
      ) : null}

      {/* Cancel Assignment Confirmation Dialog (No reason required for pending assignment) */}
      <Dialog
        open={Boolean(cancelAssignmentTarget)}
        onClose={() => !cancelAssignmentLoading && setCancelAssignmentTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, fontSize: 17, color: "error.main" }}>
          Cancelar asignación
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: "neutral.700" }}>
            ¿Estás seguro de que deseas cancelar la asignación de{" "}
            <strong>{cancelAssignmentTarget?.employee?.fullName}</strong> para la fecha{" "}
            <strong>{formatDate(cancelAssignmentTarget?.operationalDate)}</strong>?
          </Typography>
          {cancelAssignmentError ? (
            <Alert severity="error" sx={{ borderRadius: "10px", mt: 1.5 }}>
              {cancelAssignmentError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            disabled={cancelAssignmentLoading}
            onClick={() => setCancelAssignmentTarget(null)}
            sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 700 }}
          >
            Volver
          </Button>
          <Button
            disabled={cancelAssignmentLoading}
            onClick={handleConfirmCancelAssignment}
            variant="contained"
            color="error"
            sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
          >
            {cancelAssignmentLoading ? <CircularProgress size={20} /> : "Confirmar cancelación"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
